import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, RefreshCw, Search, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common";
import { UserDialog, EMPTY_USER } from "@/components/sistema/UserDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime, maskPhone, ROLE_LABELS, waLink } from "@/lib/api";
import { toast } from "sonner";

const accessMessage = (t, u) => [
  `Olá, ${u.name}! Seu acesso ao sistema ${t.nome_fantasia}:`, "",
  `Número de acesso: ${t.access_number}`, `Login: ${u.login}`, "Senha: a que foi cadastrada para você", "",
  `Entre em: ${window.location.origin}/login`,
].join("\n");

export default function UsersPage() {
  const { session } = useAuth();
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); api.get("/stores").then((r) => setStores(r.data)).catch(() => {}); }, [load]);
  const shown = users.filter((u) => `${u.name} ${u.login} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <PageHeader eyebrow="Sistema" title="Usuários" subtitle="Cadastre quem usa o sistema, com e-mail de recuperação, WhatsApp, senha e permissões de acesso." actions={<>
        <Button variant="outline" onClick={load} data-testid="users-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />Atualizar</Button>
        <Button onClick={() => setEdit(EMPTY_USER)} data-testid="new-user-btn"><Plus className="h-4 w-4 mr-1" />Novo usuário</Button>
      </>} />
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, login ou e-mail" className="pl-9" data-testid="users-search-input" />
      </div>
      <div className="card-surface overflow-x-auto">
        <Table data-testid="users-list">
          <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Login</TableHead><TableHead>E-mail</TableHead><TableHead>WhatsApp</TableHead><TableHead>Nível</TableHead><TableHead>Último acesso</TableHead><TableHead>Situação</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {shown.map((u) => (
              <TableRow key={u.id} className={u.active ? "" : "opacity-50"} data-testid={`user-row-${u.login}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="font-mono text-xs">{u.login}</TableCell>
                <TableCell>{u.email || "—"}</TableCell>
                <TableCell>{u.whatsapp ? maskPhone(u.whatsapp) : "—"}</TableCell>
                <TableCell>{ROLE_LABELS[u.role]}{u.screen_areas && <span className="ml-1 text-[10px] text-primary">personalizado</span>}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{u.last_login_at ? fmtDateTime(u.last_login_at) : "nunca"}</TableCell>
                <TableCell>{u.active ? "Ativo" : "Inativo"}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setEdit(u)} data-testid={`edit-user-${u.login}`}><Pencil className="h-3.5 w-3.5 mr-1" />Editar</Button>
                  {u.whatsapp && <Button asChild size="icon" variant="ghost" data-testid={`send-access-${u.login}`}><a href={waLink(u.whatsapp, accessMessage(session.tenant, u))} target="_blank" rel="noreferrer" aria-label="Enviar acesso por WhatsApp"><MessageCircle className="h-4 w-4 text-emerald-500" /></a></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <UserDialog user={edit} users={users} stores={stores} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </>
  );
}
